import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { FAQ } from '@/data/faq';
import { ChevronDown, MessageSquare, ArrowRight, Sparkles } from 'lucide-react';

const COPY = {
  it: {
    eyebrow: 'FAQ',
    title_a: 'Le risposte chiare,',
    title_b: 'prima della call.',
    lead:
      'Costi, adozione, sicurezza, formazione, integrazioni: risposte chiare alle domande che bloccano una scelta importante.',
    closingTitle:
      'Le ultime domande si tolgono in 30 minuti, non leggendo.',
    closingBody:
      'In una demo guidata vediamo il tuo flusso reale e ti diciamo subito se Prime Vision risolve i tuoi 3 punti più critici. Senza giri di parole.',
    ctaPrimary: 'Vedi Prime Vision in azione',
    ctaSecondary: 'Chiedi all\'assistente AI',
  },
  en: {
    eyebrow: 'FAQ',
    title_a: 'Clear answers,',
    title_b: 'before the call.',
    lead:
      'Costs, adoption, security, training, integrations: clear answers to the questions that block an important decision.',
    closingTitle:
      'The final questions get answered in 30 minutes, not by reading.',
    closingBody:
      'In a guided demo we look at your real workflow and tell you immediately whether Prime Vision solves your 3 most critical pain points. No fluff.',
    ctaPrimary: 'See Prime Vision in action',
    ctaSecondary: 'Ask the AI assistant',
  },
};

export const FAQSection = ({ compact = false }) => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;
  const fullList = FAQ[locale] || FAQ.it;
  // Cap to the 8 strongest objections (the FAQ data file orders them
  // by priority already; if you need to re-order, adjust /app/frontend/src/data/faq.js)
  const list = fullList.slice(0, 8);
  const [openId, setOpenId] = useState(list[0]?.id || null);

  const waUrl = locale === 'en'
    ? 'https://wa.me/393520526856?text=Hi!%20I%27d%20love%20to%20know%20more%20about%20Prime%20Vision.'
    : 'https://wa.me/393520526856?text=Ciao!%20Vorrei%20informazioni%20su%20Prime%20Vision.';
  const waLabel = locale === 'en' ? 'Message us on WhatsApp' : 'Scrivici su WhatsApp';

  return (
    <section
      id="faq"
      data-testid="pv-faq-section"
      className="relative bg-white py-20 md:py-28 border-t border-slate-100"
    >
      <div className="pv-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
          {/* LEFT: intro (suppressed in compact mode) */}
          {!compact && (
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">{c.eyebrow}</span>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-[30px] sm:text-[36px] md:text-[42px] leading-[1.08] text-pv-ink">
              {c.title_a}
              <br />
              <span className="text-pv-teal">{c.title_b}</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[15px] md:text-base leading-relaxed">{c.lead}</p>

            <div className="mt-7 flex items-center gap-3">
              <div className="flex flex-col">
                <div className="font-heading font-bold text-3xl md:text-4xl text-pv-teal tracking-normal">
                  {list.length}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">
                  {locale === 'en' ? 'top questions' : 'risposte chiare'}
                </div>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 font-bold hover:text-[#1FB755] transition-colors"
                data-testid="pv-faq-whatsapp-link"
              >
                <MessageSquare className="w-4 h-4" />
                {waLabel}
              </a>
            </div>
          </div>
          )}

          {/* RIGHT: accordion (full width in compact mode) */}
          <div className={compact ? 'lg:col-span-12' : 'lg:col-span-8'}>
            <div className="space-y-3">
              {list.map((item, idx) => {
                const isOpen = openId === item.id;
                return (
                  <article
                    key={item.id}
                    data-testid={`pv-faq-${item.id}`}
                    className={`relative bg-white rounded-2xl border transition-all ${
                      isOpen
                        ? 'border-pv-teal/40 shadow-[0_20px_50px_-24px_rgba(27,127,184,0.30)]'
                        : 'border-slate-200 shadow-[0_4px_14px_-10px_rgba(15,23,42,0.10)] hover:border-pv-teal/30'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenId(isOpen ? null : item.id)}
                      data-testid={`pv-faq-toggle-${item.id}`}
                      aria-expanded={isOpen}
                      className="w-full flex items-start gap-4 p-5 md:p-6 text-left group"
                    >
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-pv-ice border border-pv-teal/25 font-mono text-[11px] font-bold text-pv-teal tracking-normal">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 font-heading font-bold text-[15.5px] md:text-[17px] leading-tight text-pv-ink tracking-normal pt-1">
                        {item.q}
                      </span>
                      <ChevronDown
                        className={`shrink-0 w-5 h-5 text-slate-400 transition-transform mt-1 ${
                          isOpen ? 'rotate-180 text-pv-teal' : 'rotate-0'
                        }`}
                      />
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-5 md:px-6 pb-5 md:pb-6 pl-[72px] md:pl-[76px]">
                          <p className="text-slate-700 text-[14.5px] md:text-[15px] leading-[1.65] whitespace-pre-line">
                            {item.a}
                          </p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Closing CTA card */}
            <div
              data-testid="pv-faq-closing-cta"
              className="relative mt-8 bg-white rounded-3xl border border-slate-200 p-7 md:p-9 overflow-hidden shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)]"
            >
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-pv-orange/15 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-44 h-44 rounded-full bg-pv-teal/10 blur-3xl" />

              <div className="relative flex items-start gap-3 mb-4">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-pv-peach border border-pv-orange/30 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-pv-orange" />
                </div>
                <div>
                  <span className="pv-mono-tag text-pv-orangeDeep">
                    {locale === 'en' ? "Don't see your question?" : 'Non trovi la tua domanda?'}
                  </span>
                </div>
              </div>

              <h3 className="relative font-heading font-bold text-[22px] sm:text-[26px] md:text-[28px] leading-[1.2] text-pv-ink tracking-normal">
                {c.closingTitle}
              </h3>
              <p className="relative mt-4 text-slate-600 text-[14.5px] md:text-[15px] leading-relaxed">
                {c.closingBody}
              </p>

              <div className="relative mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="/bookacall"
                  data-testid="pv-faq-cta-demo"
                  className="pv-btn-primary group"
                >
                  {c.ctaPrimary}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pv-faq-cta-whatsapp"
                  className="pv-btn-whatsapp group"
                >
                  <MessageSquare className="w-4 h-4" />
                  {waLabel}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
